import { useEffect, useRef, type KeyboardEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { modeOf, type OpsMode } from '@/pages/Operations/opsParams'

const MODES: [OpsMode, string][] = [
  ['ours', 'Our operations'],
  ['atlassian', 'All Atlassian API'],
]

const DEFAULT_SEARCH: Record<OpsMode, string> = { ours: '', atlassian: '?mode=atlassian' }

// Each mode keeps its own filters: leaving a mode remembers its query string, coming back
// restores it, so Atlassian filters never leak into our operations (and vice versa).
const lastSearch: Record<OpsMode, string> = { ...DEFAULT_SEARCH }
// the other mode is a different page tree: the arrow-key switch hands focus over to it
let focusOnMount = false

/** Segmented "Our operations / All Atlassian API" control for the Operations header. */
export function ModeSwitch({ mode }: { mode: OpsMode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    if (!focusOnMount) return
    focusOnMount = false
    refs.current[MODES.findIndex(([m]) => m === mode)]?.focus()
  }, [mode])

  const go = (next: OpsMode) => {
    if (next === mode) return
    lastSearch[mode] = location.search
    const target = lastSearch[next]
    // a remembered search must still belong to its mode
    const search = modeOf(new URLSearchParams(target)) === next ? target : DEFAULT_SEARCH[next]
    navigate({ pathname: '/operations', search })
  }

  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const j = (i + (e.key === 'ArrowRight' ? 1 : MODES.length - 1)) % MODES.length
    focusOnMount = true
    go(MODES[j][0])
  }

  return (
    <div role="radiogroup" aria-label="Catalog" className="flex shrink-0 rounded-md border border-line-strong p-px">
      {MODES.map(([m, label], i) => {
        const on = m === mode
        return (
          <button
            key={m}
            ref={(el) => { refs.current[i] = el }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => go(m)}
            onKeyDown={(e) => onKey(e, i)}
            className={[
              'h-[26px] whitespace-nowrap rounded-[5px] px-2.5 text-[12.5px] transition-colors',
              on ? 'bg-surface-active font-medium text-foreground' : 'text-text-2 hover:text-foreground',
            ].join(' ')}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
